import React, { Suspense, lazy } from "react";
import { useCurrentCompany } from "hooks/auth";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { NoAccessMessage, ReadOnlySurface } from "newui/components/AccessGate";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { RBAC_MODULE_KEYS } from "newui/features/RolesPermissions/types/RolesPermissions.type";

const PdfSettingsLazy = lazy(() =>
	import("./PdfSettings/PdfSettings").then((m) => ({ default: m.PdfSettings }))
);

interface PdfSettingsPageProps {
	path?: string;
}

/**
 * PRO-709 — the route gate for PDF Report Settings, keyed on the
 * `pdf_report_settings` module (split out of the monolithic `settings`).
 *
 * Gating HERE rather than inside `PdfSettings` is what makes the route
 * unreachable by URL: a user without the capability never mounts the lazy
 * chunk, so none of its design-schema queries fire and nothing 403s.
 */
export const PdfSettingsPage: React.FC<PdfSettingsPageProps> = () => {
	const company = useCurrentCompany();
	const profileContext = useProfileContext();
	const accountType = profileContext?.currentProfile === CurrentAccountType.PROCURER ? "procurer" : "supplier";
	const { canView, canEdit, isLoading: isPermissionsLoading } = useModuleCan(
		RBAC_MODULE_KEYS.pdfReportSettings
	);

	if (!company || isPermissionsLoading) {
		return <LoadingSpinner label="Loading..." verticalPadding={8} />;
	}

	if (!canView && !canEdit) {
		return (
			<NoAccessMessage
				data-testid="pdf-settings-no-access"
				description="You do not have permission to view PDF Report Settings. Ask a company administrator if you need access."
			/>
		);
	}

	const content = (
		<Suspense
			fallback={
				<LoadingSpinner label="Loading PDF Report Settings..." verticalPadding={8} />
			}
		>
			<PdfSettingsLazy companyId={company.id} accountType={accountType} />
		</Suspense>
	);

	if (!canEdit) {
		return (
			<ReadOnlySurface
				data-testid="pdf-settings-read-only"
				message="You have read-only access to PDF Report Settings. Saving, resetting and uploads are disabled."
			>
				{content}
			</ReadOnlySurface>
		);
	}

	return content;
};
